import { uruguayRequiredDocuments } from '../mocks/UruguayRequiredDocuments';
import type { AppConfig } from '../types/config.types';
import type { documentData } from '../types/interfaces';

export const BALANCE_DOCUMENT_ID = '009';
export const PREVIOUS_BALANCE_DOCUMENT_ID = '103';

export const PREVIOUS_BALANCE_DOCUMENT_LABEL =
  'Anteultimo Estado Contable Certificado';

export type OriginationConditionalFeature = 'originationPreviousBalance';

export type OriginationRequiredDocumentsConfig = Pick<
  AppConfig,
  'isOriginationPreviousBalanceEnabled' | 'isUruguayEnabled'
>;

export interface RequiredDocumentDef {
  id: string;
  value: string;
  conditionalFeature?: OriginationConditionalFeature;
  optional?: boolean;
}

export const PREVIOUS_BALANCE_REQUIRED_DOCUMENT: RequiredDocumentDef = {
  id: PREVIOUS_BALANCE_DOCUMENT_ID,
  value: PREVIOUS_BALANCE_DOCUMENT_LABEL,
  conditionalFeature: 'originationPreviousBalance',
  optional: true,
};

export function isBalancePdfDocumentType(documentId: string): boolean {
  return documentId === BALANCE_DOCUMENT_ID || documentId === PREVIOUS_BALANCE_DOCUMENT_ID;
}

export function fillsOriginationRequiredSlot(
  doc: Pick<documentData, 'type' | 'file_name' | 'is_basic_data'>,
  requiredId: string
): boolean {
  if (doc.type !== requiredId || !doc.file_name) {
    return false;
  }
  if (isBalancePdfDocumentType(requiredId)) {
    return true;
  }
  return Boolean(doc.is_basic_data);
}

export function findDocumentForRequiredSlot(
  documents: documentData[],
  requiredId: string
): documentData | undefined {
  const matches = documents.filter(
    (doc) =>
      doc?.type === requiredId &&
      doc.file_name &&
      fillsOriginationRequiredSlot(doc, requiredId)
  );

  if (matches.length === 0) {
    return undefined;
  }

  return isBalancePdfDocumentType(requiredId)
    ? matches[matches.length - 1]
    : matches[0];
}

export function isOriginationExtraDocument(
  doc: documentData,
  requiredDocuments: RequiredDocumentDef[]
): boolean {
  if (!doc.type || !doc.file_name) {
    return false;
  }
  if (isBalancePdfDocumentType(doc.type)) {
    return false;
  }
  return !requiredDocuments.some((req) => fillsOriginationRequiredSlot(doc, req.id));
}

export function filterRequiredDocuments(
  docs: RequiredDocumentDef[],
  config: OriginationRequiredDocumentsConfig
): RequiredDocumentDef[] {
  return docs.filter((doc) => {
    if (doc.conditionalFeature === 'originationPreviousBalance') {
      return config.isOriginationPreviousBalanceEnabled ?? false;
    }
    return true;
  });
}

export function shouldShowOptionalDocumentInSummary(
  doc: RequiredDocumentDef,
  files: documentData[]
): boolean {
  if (!doc.optional) {
    return true;
  }
  return Boolean(findDocumentForRequiredSlot(files, doc.id));
}

export function getRequiredDocumentsForLegalEntity(
  legalEntityLabel: string,
  legalEntities: Array<{ label: string; requiredDocuments: RequiredDocumentDef[] }>,
  config: OriginationRequiredDocumentsConfig
): RequiredDocumentDef[] {
  if (config.isUruguayEnabled) {
    return uruguayRequiredDocuments;
  }

  const entity = legalEntities.find((item) => item.label === legalEntityLabel);
  if (!entity) {
    return [];
  }
  return filterRequiredDocuments(entity.requiredDocuments, config);
}
